/**
 * Homly — KPIWithExplanation
 * ==========================
 * Affiche un KPI avec une phrase humaine qui explique CE QUE ça veut dire.
 *
 * Le brief : "Au lieu d'afficher uniquement 'Taux d'activité vendeur : 42 %',
 *             ajouter une phrase claire comme : Ce vendeur consulte peu son espace.
 *             Une relance humaine peut renforcer son engagement."
 *
 * Pattern :
 *   - Label en haut (TRÈS COURT, MAJUSCULES, gris)
 *   - Valeur centrale (gros, mono)
 *   - Variation (verte ↑ rouge ↓) si fournie
 *   - Phrase humaine en bas qui interprète le chiffre
 */
import React from 'react';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';

export function KPIWithExplanation({
    label,
    value,
    unit = '',
    trend,                // { value: number, isPositiveGood: boolean }
    explanation,          // phrase humaine — toujours présente
    icon: Icon,
    accent = 'purple',    // 'purple' | 'orange' | 'green' | 'red'
    size = 'md',          // 'sm' | 'md' | 'lg'
    onClick,
    className = '',
    dataTestid = 'kpi-card',
}) {
    const accentColors = {
        purple: 'var(--homly-purple)',
        orange: 'var(--homly-orange)',
        green: 'var(--homly-priority-ok)',
        red: 'var(--homly-priority-critical)',
        gray: 'var(--homly-text-tertiary)',
    };
    const accentColor = accentColors[accent] || accentColors.purple;

    const sizes = {
        sm: { valueFontSize: 22, labelFontSize: 10, padding: 14 },
        md: { valueFontSize: 30, labelFontSize: 11, padding: 18 },
        lg: { valueFontSize: 38, labelFontSize: 12, padding: 22 },
    };
    const s = sizes[size] || sizes.md;

    // Détermine si la variation est "bonne" ou "mauvaise"
    let trendColor = 'var(--homly-text-tertiary)';
    let TrendIcon = Minus;
    if (trend != null && trend.value != null) {
        if (trend.value > 0) {
            TrendIcon = TrendingUp;
            trendColor = trend.isPositiveGood !== false ? 'var(--homly-priority-ok)' : 'var(--homly-priority-critical)';
        } else if (trend.value < 0) {
            TrendIcon = TrendingDown;
            trendColor = trend.isPositiveGood !== false ? 'var(--homly-priority-critical)' : 'var(--homly-priority-ok)';
        }
    }

    const clickable = !!onClick;

    return (
        <article
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            data-testid={dataTestid}
            className={className}
            style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--homly-space-3)',
                padding: s.padding,
                background: 'var(--homly-bg-elevated)',
                border: '1px solid var(--homly-border)',
                borderRadius: 'var(--homly-radius-lg)',
                cursor: clickable ? 'pointer' : 'default',
                transition: 'all var(--homly-transition-base)',
                fontFamily: 'var(--homly-font-body)',
                minHeight: 140,
                overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
                if (!clickable) return;
                e.currentTarget.style.borderColor = `${accentColor}55`;
                e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
                if (!clickable) return;
                e.currentTarget.style.borderColor = 'var(--homly-border)';
                e.currentTarget.style.transform = 'translateY(0)';
            }}
        >
            {/* Header : icône + label */}
            <header
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                {Icon && (
                    <div
                        style={{
                            width: 24, height: 24,
                            borderRadius: 'var(--homly-radius-sm)',
                            background: `${accentColor}1f`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: accentColor,
                            flexShrink: 0,
                        }}
                    >
                        <Icon size={13} strokeWidth={2.4} aria-hidden="true" />
                    </div>
                )}
                <span
                    style={{
                        fontFamily: 'var(--homly-font-display)',
                        fontSize: s.labelFontSize,
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: 'var(--homly-text-tertiary)',
                    }}
                >
                    {label}
                </span>
            </header>

            {/* Valeur centrale */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    flexWrap: 'wrap',
                }}
            >
                <span
                    style={{
                        fontFamily: 'var(--homly-font-mono)',
                        fontSize: s.valueFontSize,
                        fontWeight: 700,
                        color: 'var(--homly-text-primary)',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                    }}
                >
                    {value}
                    {unit && (
                        <span
                            style={{
                                fontSize: s.valueFontSize * 0.55,
                                color: 'var(--homly-text-tertiary)',
                                marginLeft: 4,
                                fontWeight: 500,
                            }}
                        >
                            {unit}
                        </span>
                    )}
                </span>

                {trend != null && trend.value != null && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                            padding: '2px 7px',
                            background: `${trendColor}1a`,
                            color: trendColor,
                            borderRadius: 'var(--homly-radius-pill)',
                            fontFamily: 'var(--homly-font-mono)',
                            fontSize: 11,
                            fontWeight: 700,
                        }}
                    >
                        <TrendIcon size={11} strokeWidth={2.5} />
                        {trend.value > 0 ? '+' : ''}{trend.value}{trend.unit || '%'}
                    </span>
                )}
            </div>

            {/* Explanation : LA phrase humaine qui rend le chiffre actionnable */}
            {explanation && (
                <p
                    style={{
                        margin: 0,
                        fontSize: 12.5,
                        lineHeight: 1.5,
                        color: 'var(--homly-text-secondary)',
                        display: 'flex',
                        gap: 6,
                        alignItems: 'flex-start',
                    }}
                >
                    <Info size={12} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 2, color: 'var(--homly-text-tertiary)' }} aria-hidden="true" />
                    <span>{explanation}</span>
                </p>
            )}
        </article>
    );
}
